import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
    const [credentials, setCredentials] = useState({
        email: '',
        password: '',
        captcha_answer: ''
    });
    const [captcha, setCaptcha] = useState({ question: '', captcha_id: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login, getCaptcha, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadCaptcha();
    }, []);

    // Redirect if already authenticated
    useEffect(() => {
        if (user) {
            navigate('/', { replace: true });
        }
    }, [user, navigate]);

    const loadCaptcha = async () => {
        try {
            const captchaData = await getCaptcha();
            setCaptcha(captchaData);
        } catch (error) {
            setError('Erreur lors du chargement du captcha');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!credentials.email || !credentials.password || !credentials.captcha_answer) {
            setError('Tous les champs sont requis');
            setLoading(false);
            return;
        }

        try {
            const result = await login({
                email: credentials.email,
                password: credentials.password,
                captcha_id: captcha.captcha_id,
                captcha_answer: parseInt(credentials.captcha_answer)
            });

            if (result.success) {
                // Navigation will happen automatically via the useEffect above
                // when user state is updated
                navigate('/', { replace: true });
            } else {
                setError(result.error);
                // Recharger un nouveau captcha après échec
                await loadCaptcha();
                setCredentials(prev => ({ ...prev, captcha_answer: '' }));
            }
        } catch (error) {
            setError('Erreur de connexion');
            await loadCaptcha();
            setCredentials(prev => ({ ...prev, captcha_answer: '' }));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setCredentials(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img 
                        src="/voxipro_logo_detoure.png" 
                        alt="VoxiPro Logo" 
                        className="mx-auto h-24 w-auto mb-4"
                    />
                    <h1 className="text-2xl font-bold text-gray-800">VoxiPro AI</h1>
                    <p className="text-gray-600 mt-2">Authentification requise</p>
                </div>

                {/* Formulaire de connexion */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Identifiant
                        </label>
                        <input
                            type="text"
                            id="email"
                            name="email"
                            value={credentials.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                            Mot de passe
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={credentials.password}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="captcha" className="block text-sm font-medium text-gray-700 mb-2">
                            Calcul mathématique
                        </label>
                        <div className="bg-gray-50 p-3 rounded-md mb-2">
                            <div className="text-lg font-mono text-center">
                                {captcha.question} = ?
                            </div>
                        </div>
                        <input
                            type="number"
                            id="captcha"
                            name="captcha_answer"
                            value={credentials.captcha_answer}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Entrez votre réponse"
                            required
                        />
                        <button
                            type="button"
                            onClick={loadCaptcha}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                            Nouveau calcul
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;